import { useLingui } from '@lingui/react/macro'
import { Tooltip } from 'antd'
import { isPermittedByGate } from 'features/permissions/utils/isPermittedByGate'
import React from 'react'
import styled from 'styled-components'
import { RolodexPermissionsValues } from 'types'

import { useGeckoPermissions } from '..'
const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto;
  justify-content: center;
  align-items: center;
`

const StyledTooltip = styled(Tooltip)`
  &. {
    font-size: 10px;
  }
`

const BackgroundOverlay = styled.div<{ $hasPermissions: boolean }>`
  background: repeating-linear-gradient(
    45deg,
    ${(props) => (props.$hasPermissions ? '#0F8A0088' : '#BF00008E')},
    ${(props) => (props.$hasPermissions ? '#0F8A0088' : '#BF00008E')} 5px,
    transparent 5px,
    transparent 10px
  );
  display: inherit;
`

const ClickOverlay = styled.span<{ $hasPermissions: boolean }>`
  pointer-events: ${(props) => (props.$hasPermissions ? 'auto' : 'none')};
  display: inherit;
`

/**
 * Component displayed when a user lacks the required permissions to access a page
 */
export const MissingPermissionsPageComponent = () => {
  const { t } = useLingui()
  return (
    <Container>
      <h1>{t`You do not have permissions to view this page`}</h1>
      <h3>{t`Consult with your admin for gaining access`}</h3>
    </Container>
  )
}

export interface PermissionsGateProps {
  requires?: RolodexPermissionsValues[]
  except?: RolodexPermissionsValues[]
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * A component that gates content behind permission checks.
 *
 * The PermissionsGate component provides a way to restrict access to content based on user permissions.
 * - Simply render the children if permissions are met
 * - Render the fallback content (if provided) if permissions are not met
 *
 * When "Show Permission Gates" is enabled in the Admin Console,
 * this component will display a striped background overlay over the element it is gating
 *
 * @example
 * ```tsx
 * <PermissionsGate
 *   requires={[RolodexPermissions.ADMIN_ACCESS]}
 *   fallback={<MissingPermissionsPageComponent />}
 * >
 *   <ProtectedContent />
 * </PermissionsGate>
 * ```
 *
 * @param {Object} props - The component props
 * @param {RolodexPermissions[]} props.requires - Array of permissions required to access the protected content.
 *                                               This is an OR check, so if the user has any of the permissions,
 *                                               they will be able to access the content. This is not overriden
 *                                               by gecko permissions to prevent cluttering.
 * @param {RolodexPermissions[]} props.except - Array of permissions that deny access to the protected content.
 *                                               This is an OR check, so if the user has any of the permissions,
 *                                               they will be unable to access the content. However, if the user
 *                                               has any of the gecko permissions, they will be able to bypass
 *                                               this restriction. This is to prevent gecko users from being
 *                                               blocked from features by weaker permissions.
 * @param {React.ReactNode} props.children - The content to be protected by the permissions gate
 * @param {React.ReactNode} [props.fallback] - Optional content to display when permissions are not met
 * @returns {JSX.Element} The rendered component
 */
export const PermissionsGate = (props: PermissionsGateProps) => {
  const { requires, except, children, fallback } = props
  const { t } = useLingui()

  const { hasPermissions, showPermissionGates } = useGeckoPermissions()
  const isAllowed = isPermittedByGate(hasPermissions, { requires, except })

  const renderPermissionsList = (
    title: string,
    permissions: RolodexPermissionsValues[] | undefined
  ) => {
    if (!permissions || permissions.length === 0) {
      return null
    }
    return (
      <>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
          }}>
          {title}
        </div>
        {permissions.map((permission) => (
          <div key={permission} style={{ fontSize: '12px' }}>
            • {permission}
          </div>
        ))}
      </>
    )
  }

  if (showPermissionGates) {
    return (
      <StyledTooltip
        title={
          <>
            {renderPermissionsList(t`Requires any of:`, requires)}
            {renderPermissionsList(t`Hidden for any of:`, except)}
          </>
        }>
        <BackgroundOverlay $hasPermissions={isAllowed}>
          <ClickOverlay $hasPermissions={isAllowed}>{children}</ClickOverlay>
        </BackgroundOverlay>
      </StyledTooltip>
    )
  } else if (isAllowed) {
    return children
  } else {
    return <>{fallback && fallback}</>
  }
}
